// The supplier's delivery CAPABILITY — asked once, reused on every quote.
//
// ── Why this is not just price_basis ────────────────────────────────────────
// offers.price_basis is a PROMISE about one deal. profiles.delivery_terms is
// what this supplier can do at all. Conflating them is what made the old form
// ask the same question on every quote and store the answer nowhere: a factory
// that only ever ships FOB re-picked FOB every time, and we never learned it.
//
// Storing the capability lets the quote form open already answered, which is
// the difference between a field and a chore — and every extra field on the
// quote form costs completions among exactly the suppliers the public RFQ
// links exist to recruit.
//
// ── Why it is NOT on supplier_public_profiles ───────────────────────────────
// A capability is not an offer. If a trader could browse "suppliers who
// deliver to my door", the list would be a promise nobody priced. Only the
// per-offer price_basis is a promise, and it is made deal by deal.
// (Migration 20260921122556 says the same thing on the column itself.)

import { PRICE_BASES } from '../components/quote/QuoteFields';

/** The order the options are offered in: least to most carried by the supplier. */
export const DELIVERY_TERM_ORDER = ['EXW', 'FOB', 'CIF', 'DDP'];

export const DELIVERY_TERM_LABELS = {
  EXW: {
    ar: { title: 'من باب المصنع', hint: 'المشتري يرتّب الشحن بنفسه' },
    en: { title: 'From my factory door', hint: 'The buyer arranges shipping' },
    zh: { title: '工厂交货', hint: '由买家自行安排运输' },
  },
  FOB: {
    ar: { title: 'إلى ميناء الصين', hint: 'أوصلها للميناء وأحمّلها' },
    en: { title: 'To the Chinese port', hint: 'I deliver to the port and load it' },
    zh: { title: '送到中国港口', hint: '我负责送到港口并装船' },
  },
  CIF: {
    ar: { title: 'إلى ميناء الوصول', hint: 'أتحمّل الشحن البحري والتأمين' },
    en: { title: 'To the arrival port', hint: 'I cover sea freight and insurance' },
    zh: { title: '送到目的港', hint: '我承担海运和保险' },
  },
  DDP: {
    ar: { title: 'إلى عنوان المشتري', hint: 'أتحمّل الشحن والرسوم والتوصيل' },
    en: { title: "To the buyer's address", hint: 'I cover freight, duties and delivery' },
    zh: { title: '送到买家地址', hint: '我承担运费、关税和配送' },
  },
};

export const deliveryTermLabel = (code, lang) =>
  (DELIVERY_TERM_LABELS[code] || {})[lang] || (DELIVERY_TERM_LABELS[code] || {}).en || null;

/**
 * Normalise whatever came back from the column into a clean, ordered array.
 * POSITIVE FILTER: a value the app does not know is dropped rather than
 * offered, so a code added to the column by a future migration cannot appear
 * in a dropdown with no label (golden rule 12).
 */
export function normalizeDeliveryTerms(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set(
    raw.map((v) => String(v ?? '').trim().toUpperCase()).filter((v) => PRICE_BASES.includes(v)),
  );
  return DELIVERY_TERM_ORDER.filter((c) => seen.has(c));
}

/**
 * Which bases this supplier's quote form should offer.
 * An EMPTY capability means "we have not asked yet" — and then every basis is
 * offered, because narrowing a supplier's options based on a question they
 * were never asked is worse than asking again.
 */
export function allowedBasesFor(terms) {
  const clean = normalizeDeliveryTerms(terms);
  return clean.length ? clean : PRICE_BASES;
}

/**
 * What to prefill price_basis with.
 * Only when there is exactly ONE capability: a supplier who can do two things
 * must choose per deal, and pre-picking one for them is how a wrong basis ends
 * up on a quote nobody reread. Returns '' otherwise.
 */
export function defaultBasisFor(terms) {
  const clean = normalizeDeliveryTerms(terms);
  return clean.length === 1 ? clean[0] : '';
}

/**
 * Read the signed-in supplier's capability. Never throws: a failure here must
 * not stop them quoting, it only costs the prefill.
 */
export async function loadDeliveryTerms(sb, userId) {
  if (!userId) return [];
  const { data, error } = await sb
    .from('profiles').select('delivery_terms').eq('id', userId).maybeSingle();
  if (error) {
    // Security rule 4: name the provider's own message, never swallow it.
    console.error('[supplierDeliveryTerms] load failed:', error.code || '', error.message || error);
    return [];
  }
  return normalizeDeliveryTerms(data?.delivery_terms);
}

/**
 * Save the capability. Written straight to profiles — delivery_terms is NOT
 * one of the fields guard_profile_sensitive_fields protects (that guards role,
 * status, rating, reviews_count, trust_score and maabar_supplier_id), so no
 * RPC is needed and none should be invented.
 *
 * @returns {Promise<boolean>} whether it stuck; callers treat false as "carry
 *   on without the prefill", never as a reason to block the quote.
 */
export async function saveDeliveryTerms(sb, userId, terms) {
  const clean = normalizeDeliveryTerms(terms);
  if (!userId || clean.length === 0) return false;
  const { error } = await sb.from('profiles').update({ delivery_terms: clean }).eq('id', userId);
  if (error) {
    console.error('[supplierDeliveryTerms] save failed:', error.code || '', error.message || error);
    return false;
  }
  return true;
}
